import { LitElement, html, css } from 'lit';
import {
  layoutVertical,
  layoutHorizontal,
  layoutFlex,
  layoutCenter,
  layoutStartJustified,
  layoutSelfStart,
} from './layout-styles.js';


/**
 * A helper class that will wrap the form with our partial ajax loader,
 * ostinato-fetch.
 */
class XHRForm extends LitElement {

  static get properties() {
    return {
      formSelector: String,
      loadMessage: String,
      submitSelector: String,
      captchaAction: String,
      baseNode: Object,
    };
  }

  constructor() {
    super();
    this.formSelector = 'form[xhr-form]';
    this.loadMessage = '';
    this.submitSelector = '[xhr-submit]';
    this.captchaAction = null;
    this.baseNode = document;
  }

  render() {
    return html``;
  }

  firstUpdated() {
    super.firstUpdated();

    this.xhr = document.createElement('fragment-fetch');
    this.xhr.method = 'POST';
    this.xhr.targetSelectors = this.formSelector;

    this.form = this.baseNode.querySelector(this.formSelector);
    this._initForm();

    // Add event listeners
    this.xhr.addEventListener('request-started', () => {
      this.form.querySelector('work-indicator').show();
    });

    this.xhr.addEventListener('request-completed', (ev) => {
      this.form.querySelector('work-indicator').hide();

      // Deal with redirects or errors
      if (ev.detail.response.redirected) {
        document.webapp.navigate(ev.detail.response.url);
      } else if (ev.detail.response.status !== 200) {
        this.form.innerHTML = '<p class="error">Error: Form could not be submitted. Please try again later.</p>';
      }
    });

    this.xhr.addEventListener('content-updated', (ev) => {
      this._renderLoader();
      // Since this will now contain newly created nodes we have to re-attach
      // event listeners
      this._updateFormEvents();
    });
  }

  _initForm() {
    this._renderLoader();
    this._updateFormEvents();
  }

  /**
    * Adds the loading indicator markup if required
    */
  _renderLoader() {
    if (!this.form.querySelector('work-indicator')) {
      let loader = document.createElement('work-indicator');
      loader.setAttribute('active', true);
      loader.innerHTML = this.loadMessage;
      this.form.appendChild(loader);
    }
  }

  submitForm() {
    var formData = new FormData(this.form);

    this.form.querySelector('work-indicator').show();

    if (this.captchaAction) {
      document.webapp.captchaExecute(this.captchaAction).then(
        (token) => {
          formData.append('g-recaptcha-response', token);
          this.xhr.fetch(this.form.action, {
            body: formData,
            credentials: 'same-origin',
          });
        },
        (reason) => { console.log(reason); }
      );
    } else {
      this.xhr.fetch(this.form.action, {
        body: formData,
        credentials: 'same-origin',
      });
    }
  }

  _updateFormEvents() {
    var submitActor = this.form.querySelector(this.submitSelector);
    if (submitActor) {
      submitActor.addEventListener('click', (ev) => {
        ev.preventDefault();
        this.submitForm();
      });
    }
  }
}

// Only register the element if it's not already registered.
if (typeof customElements.get('xhr-form') === 'undefined') {
  customElements.define('xhr-form', XHRForm);
}


// Form fields
class FormField extends LitElement {

  static get properties() {
    return {
      errorList: { type: Array },
      required: { type: Boolean },

      icon: {
        type: String,
        reflect: true,
      },

      inputType: {
        type: String,
        reflect: true
      },

      errorList: { type: Array },

      focussed: {
        type: Boolean,
        reflect: true,
      },

      baseNode: {
        type: Object,
      },
    };
  }

  constructor() {
    super();
    this.errorList = [];
    this.icon = null;
    this.required = false;
    this.focussed = false;
    this.baseNode = document;
  }

  static get styles() {
    return css`
      :host {
        --label-fg: var(--form-field-label-fg);
        --label-size: var(--form-field-label-size, 16px);

        --input-font: var(--form-field-input-font);
        --input-bg: var(--form-field-input-bg, transparent);
        --input-fg: var(--form-field-input-fg);

        --focussed-label-fg: var(--form-field-focussed-label-fg, var(--color-primary0));
        --invalid-input-fg: var(--form-field-invalid-input-fg, var(--color-warning));

        --required-fg: var(--form-field-required-fg, var(--color-gray3));
        --error-fg: var(--form-field-error-fg, var(--color-warning));

        position: relative;

        ${layoutVertical}
      }

      :host([focussed]) #input {
        border-bottom: 1px solid var(--focussed-label-fg);
      }

      #label {
        box-sizing: border-box;
        border: 0px none;
        position: relative;
        color: var(--label-fg);
        text-align: left;
        font-weight: bold;
        font-size: var(--label-size);
        transition: color 0.3s ease;
      }

      :host([focussed]) #label {
        color: var(--focussed-label-fg);
      }

      #input {
        border: 0px none;
        border-bottom: 1px solid var(--label-fg);
        padding-bottom: 3px;
        --mdc-icon-size: 20px;

        ${layoutHorizontal}
        ${layoutStartJustified}
        ${layoutCenter}
      }

      #input ::slotted([hidden]) { visibility: hidden; }

      :host(:not([inputType="checkbox"])) #input ::slotted(*),
      :host(:not([inputType="radio"])) #input ::slotted(*) {
        font-family: var(--input-font);
        box-sizing: border-box;
        margin: 0;
        padding: 2px;
        background-color: var(--input-bg);
        color: var(--input-fg);
        line-height: 1.2em;
        min-height: 26px;
        background-color: transparent;
        border: 0px none;
        width: 100%;
        ${layoutFlex}
      }

      #input ::slotted(:focus) {
        outline: none;
      }

      #input ::slotted(.invalid) {
        color: var(--invalid-input-fg);
      }

      #input mwc-icon {
        color: var(--label-fg);
        margin-right: 5px;
      }

      :host([inputType="textarea"]) #input mwc-icon {
        ${layoutSelfStart}
      }

      :host([inputType="radio"]) #input,
      :host([inputType="checkbox"]) #input { border-bottom: none; }

      :host([inputType="radio"]) #input ::slotted(input),
      :host([inputType="checkbox"]) #input ::slotted(input) {
        width: 0;
        height: 0;
      }

      :host([inputType="radio"]) #input mwc-icon,
      :host([inputType="checkbox"]) #input mwc-icon {
        cursor: pointer;
      }

      .required {
        color: var(--required-fg);
        font-size: smaller;
        vertical-align: baseline;
      }

      .error-list {
        list-style-type: none;
        font-size: smaller;
        margin: 5px 0 0;
        padding-left: 0;
        --mdc-icon-size: 16px;
      }

      .error-list li mwc-icon { vertical-align: bottom; }

      #help ::slotted(p) {
        margin: 0 !important;
        color: var(--label-fg) !important;
        font-size: smaller;
      }

      :host(.error) #input {
        border-bottom-color: var(--error-fg);
      }

      :host(.error) #label, .error-list {
        color: var(--error-fg);
      }

      :host(.error) .error-list { display: block; }
    `;
  }

  render() {
    var errorListTemplate = html``;
    if (this.errorList) {
      if (this.errorList.length > 0) {
        errorListTemplate = html`
          <ul class="error-list">
            ${this.errorList.map(err => html`<li><mwc-icon>error_outline</mwc-icon> ${err}</li>`)}
          </ul>
        `;
      }
    }

    return html`
      <div id="label">
        <slot name="label"></slot>
        <span class="required" ?hidden=${!this.required}>(required)</span>
      </div>

      <slot name="widgets"></slot>

      <div id="input">
        ${this.icon ? html`<mwc-icon>${this.icon}</mwc-icon>` : html`` }
        <slot name="input"></slot>
      </div>

      ${errorListTemplate}

      <div id="help">
        <slot name="help"></slot>
      </div>
    `;
  }

  resetRadioGroup() {
    let fieldSelector = `[type="radio"][name="${this.inputField.name}"]`;
    this.baseNode.querySelectorAll(fieldSelector).forEach((el) => {
      el.checked = false;
      el.parentNode.updateToggles();
    });
  }

  setRadioChecked() {
    this.resetRadioGroup();
    this.inputField.checked = true;
    this.updateToggles();
  }

  firstUpdated() {
    super.firstUpdated();

    this.inputField = this.querySelector('[slot="input"]');

    if (this.inputField.hasAttribute('required')) this.required = true;
    if (this.baseNode.activeElement == this.inputField) this.focussed = true;

    this.updateToggles();
    this._validate();

    this.addEventListener('click', (ev) => {
      let isInput = (ev.target.getAttribute('slot') == 'input');
      if (!isInput) { this.inputField.focus(); }

      if (this.inputType === 'radio') {
        this.setRadioChecked();
        this.inputField.dispatchEvent(new Event('change', {bubbles: true}));
      } else if (this.inputType === 'checkbox' && !this.required) {
        this.inputField.checked = !this.inputField.checked;
        this.updateToggles();
        this.inputField.dispatchEvent(new Event('change', {bubbles: true}));
      }
    });

    this.inputField.addEventListener('focus', () => {
      this.focussed = true;
    });

    this.inputField.addEventListener('blur', () => {
      this.focussed = false;
    });
  }

  updateToggles() {
    var icons = {
      'checkbox': ['check_box', 'check_box_outline_blank'],
      'radio': ['radio_button_checked', 'radio_button_unchecked'],
    };

    if (this.inputType === 'checkbox' || this.inputType === 'radio') {
      this.inputField.style.opacity = 0;
      if (this.inputField.checked)
        this.icon = icons[this.inputType][0];
      else
        this.icon = icons[this.inputType][1];
    }
  }

  updateComplete() {
    this._validate();
  }

  _validate() {
    if (this.errorList) {
      if (this.errorList.length > 0) this.classList.add('error');
      else this.classList.remove('error');
    } else {
      this.classList.remove('error');
    }
  }

  set value(val) {
    this.inputField.value = val;
  }

  get value() { return this.inputField.value; }

}

// Only register the element if it's not already registered.
if (typeof customElements.get('form-field') === 'undefined') {
  customElements.define('form-field', FormField);
}
